import React, { useCallback, useMemo, useState } from 'react';
import { Popover } from 'antd';
import { Icon } from '@fuxi/eevee-icon';
import classNames from 'classnames';
import checkImg from '@/assets/image/check-img.png';
import halfCheckImg from '@/assets/image/half-check.png';
import notCheckImg from '@/assets/image/not-check-img.png';
import './index.scss';

interface Props {
  list: Array<{
    value: string;
    text: string;
  }>;
  value: string[] | string;
  onChange: (value: Props['value']) => void;
  multiple?: boolean;
  allSelectText?: string;
  label?: string;
  className?: string;
}

const CustomSelect: React.FC<Props> = ({
  value,
  onChange,
  list,
  allSelectText,
  multiple = true,
  className,
  label = null,
}) => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const currentIsAllSelect = useMemo(() => value?.length === list?.length, [list, value]);

  const handleChange = useCallback(
    value => {
      onChange && onChange(value);
    },
    [onChange]
  );

  const PopoverContent = useMemo(
    () =>
      multiple ? (
        <div className={classNames('select-list')}>
          {allSelectText && (
            <div
              className={classNames('select-item')}
              onClick={() => onChange(currentIsAllSelect ? [] : list.map(item => item.value))}
            >
              {currentIsAllSelect ? (
                <img className={classNames('check-state-img')} src={checkImg} alt="" />
              ) : value?.length ? (
                <img className={classNames('check-state-img')} src={halfCheckImg} alt="" />
              ) : (
                <img className={classNames('check-state-img')} src={notCheckImg} alt="" />
              )}

              <div className={classNames('select-item-name')}>
                <div className={classNames('main-text', { isActive: currentIsAllSelect })}>{allSelectText}</div>
              </div>
            </div>
          )}
          {list?.map(item => (
            <div
              key={item.value}
              className={classNames('select-item')}
              onClick={() => {
                const currenIsChecked = (value as string[])?.some(item2 => item.value === item2);
                onChange(
                  currenIsChecked ? (value as string[])?.filter(item2 => item2 !== item.value) : [...value, item.value]
                );
              }}
            >
              <img
                className={classNames('check-state-img')}
                src={(value as string[])?.some(item2 => item.value === item2) ? checkImg : notCheckImg}
                alt=""
              />
              <div className={classNames('select-item-name')}>
                <div className={classNames('main-text', { isActive: value?.includes(item.value) })}>{item.text}</div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className={classNames('options')}>
          {list?.map(item => (
            <div
              key={item.value}
              onClick={() => handleChange(item.value)}
              className={classNames('options-item', { active: value === item.value })}
            >
              <div>{item.text}</div>
              {value === item.value && <Icon name="check" className={classNames('icon')} />}
            </div>
          ))}
        </div>
      ),
    [allSelectText, currentIsAllSelect, handleChange, list, multiple, onChange, value]
  );

  return (
    <div className={classNames('custom-select', className)}>
      {isPopoverOpen && <div className={classNames('popover-mask')} onClick={() => setIsPopoverOpen(false)} />}
      <Popover
        getPopupContainer={container => container}
        content={PopoverContent}
        title={null}
        arrow={false}
        placement="bottomLeft"
        open={isPopoverOpen}
        destroyOnHidden={true}
        trigger="click"
      >
        <div className={classNames('options-box')} onClick={() => setIsPopoverOpen(true)}>
          {multiple ? (
            <div className={classNames('wrapper-text', { isClick: isPopoverOpen })}>
              {label
                ? label
                : value?.length && value?.length < list?.length
                ? list
                    ?.filter(item => value?.includes(item.value))
                    .map(item => item.text)
                    ?.join('、')
                : allSelectText}
            </div>
          ) : (
            <div className={classNames('selected-val', { isClick: isPopoverOpen })}>
              {list?.find(item => item.value === value)?.text}
            </div>
          )}
          <Icon
            name={!isPopoverOpen ? 'arrow-down' : 'arrow-up'}
            className={classNames('icon', { isClick: isPopoverOpen })}
          />
        </div>
      </Popover>
    </div>
  );
};

export default CustomSelect;
